/**
 * Renders a deterministic `schedule_snapshot` artifact inline in Kai chat.
 *
 * Three modes reuse the app's schedule building blocks:
 * - `sailing_lookup` / `booking_matched` → a compact sailing list (CarrierLogo,
 *   ETD/ETA/transit time, direct/TS badge, match badge) that opens the shared
 *   `ScheduleSailingDetailDialog` on click.
 * - `booking_milestones` → the same `ScheduleWeekGrid` / `ScheduleDayView` /
 *   `ScheduleMonthGrid` the `/schedule` page and home tile use, fed from the
 *   artifact's booking rows.
 */
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

import { CarrierLogo } from "@/components/common/CarrierLogo";
import { Badge } from "@/components/ui/badge";
import { ScheduleEventDetailDialog } from "@/features/schedule/ScheduleEventDetailDialog";
import { ScheduleSailingDetailDialog } from "@/features/schedule/ScheduleSailingDetailDialog";
import { ScheduleDayView } from "@/features/schedule/ScheduleDayView";
import { ScheduleMonthGrid } from "@/features/schedule/ScheduleMonthGrid";
import { ScheduleWeekGrid } from "@/features/schedule/ScheduleWeekGrid";
import { apiBookingToRow } from "@/lib/mapBooking";
import { buildAreaMatcher } from "@/lib/portAreaMatchUtils";
import {
  bookingsToScheduleEvents,
  bookingsToScheduleItems,
  buildWeekDays,
  parseLocalDate,
  weekRangeLabel,
} from "@/lib/scheduleEventMapping";
import { monthBounds } from "@/lib/dateHorizon";
import type { ScheduleResponse } from "@/types/api";
import type { ScheduleEvent, ScheduleMilestone } from "@/types/schedule";
import type { KaiScheduleSnapshot as KaiScheduleSnapshotSpec } from "./kaiScheduleTypes";

function dateKey(iso: string | null | undefined): string | null {
  return iso ? iso.slice(0, 10) : null;
}

function sailingEtd(s: ScheduleResponse): string | null {
  return dateKey(s.origin_estimated_date ?? s.legs?.[0]?.departure_estimated_date);
}

function sailingEta(s: ScheduleResponse): string | null {
  const last = s.legs?.[s.legs.length - 1];
  return dateKey(s.dest_estimated_date ?? last?.arrival_estimated_date);
}

function carrierCode(s: ScheduleResponse): string {
  return s.carrier_canonical_code || s.carrier_name;
}

function formatListDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function mondayOf(iso?: string | null): Date {
  const base = (iso ? parseLocalDate(iso) : null) ?? new Date();
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  const dow = d.getDay();
  const offset = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + offset);
  return d;
}

function SailingList({
  schedules,
  showMatch,
}: {
  schedules: ScheduleResponse[];
  showMatch: boolean;
}) {
  const areaMatcher = useMemo(() => buildAreaMatcher(null), []);
  const [detail, setDetail] = useState<ScheduleResponse | null>(null);
  const [open, setOpen] = useState(false);

  if (schedules.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No sailings matched this search.
      </p>
    );
  }

  return (
    <>
      <ul className="flex flex-col gap-1">
        {schedules.map((s, i) => {
          const etd = sailingEtd(s);
          const eta = sailingEta(s);
          return (
            <li key={s.id || `${carrierCode(s)}-${i}`}>
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-md px-1.5 py-1.5 text-left hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => {
                  setDetail(s);
                  setOpen(true);
                }}
              >
                <CarrierLogo
                  carrier={carrierCode(s)}
                  size={18}
                  bold
                  className="shrink-0 text-xs"
                />
                <span className="flex min-w-0 flex-1 items-center gap-1 truncate text-[11px] text-muted-foreground">
                  {s.legs?.[0]?.vessel_name?.trim() || "—"}
                </span>
                <span className="flex shrink-0 flex-col items-end gap-0 leading-tight text-[11px] text-muted-foreground tabular-nums">
                  <span>ETD {formatListDate(etd)}</span>
                  <span>ETA {formatListDate(eta)}</span>
                  {s.transit_time != null && <span>TT {s.transit_time}d</span>}
                </span>
                {showMatch && s.matched && (
                  <Badge variant="success" className="shrink-0 px-1.5 py-0 text-[10px]">
                    Match
                  </Badge>
                )}
                <Badge
                  variant={s.direct ? "default" : "info"}
                  className="shrink-0 px-1.5 py-0 text-[10px]"
                >
                  {s.direct ? "Direct" : `TS · ${s.legs?.length ?? 0}`}
                </Badge>
              </button>
            </li>
          );
        })}
      </ul>
      <ScheduleSailingDetailDialog
        sailing={detail}
        areaMatcher={areaMatcher}
        open={open}
        onOpenChange={setOpen}
      />
    </>
  );
}

function MilestoneGrid({ spec }: { spec: KaiScheduleSnapshotSpec }) {
  const view = spec.view === "day" || spec.view === "month" ? spec.view : "week";
  const milestones: ScheduleMilestone[] =
    spec.kinds.length > 0 ? spec.kinds : ["departure"];

  const bookings = useMemo(
    () => spec.bookings.map(apiBookingToRow),
    [spec.bookings],
  );

  const [selectedEvent, setSelectedEvent] = useState<ScheduleEvent | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const openEvent = (ev: ScheduleEvent) => {
    setSelectedEvent(ev);
    setDetailOpen(true);
  };

  let grid: ReactNode;

  if (view === "day") {
    const dayIso = spec.range?.from ?? spec.context.date_from ?? undefined;
    const items = bookingsToScheduleItems(
      bookings,
      milestones,
      dayIso
        ? { from: dayIso, to: dayIso }
        : { from: "0000-01-01", to: "9999-12-31" },
    );
    grid =
      items.length === 0 ? (
        <EmptyMilestones />
      ) : (
        <ScheduleDayView
          events={items}
          selectedId={selectedEvent?.id ?? null}
          onSelect={openEvent}
        />
      );
  } else if (view === "month") {
    const anchor =
      (spec.range?.from ? parseLocalDate(spec.range.from) : null) ?? new Date();
    const bounds = monthBounds(anchor);
    const items = bookingsToScheduleItems(bookings, milestones, bounds);
    grid =
      items.length === 0 ? (
        <EmptyMilestones />
      ) : (
        <ScheduleMonthGrid
          anchor={anchor}
          events={items}
          selectedId={selectedEvent?.id ?? null}
          onSelect={openEvent}
          onSelectDay={() => {}}
        />
      );
  } else {
    const monday = mondayOf(spec.range?.from ?? spec.context.date_from);
    const events = bookingsToScheduleEvents(bookings, { monday, milestones });
    const weekDays = buildWeekDays(monday);
    grid =
      events.length === 0 ? (
        <EmptyMilestones />
      ) : (
        <div className="flex flex-col gap-1">
          <p className="px-1 text-[11px] text-muted-foreground">
            {weekRangeLabel(monday)}
          </p>
          <ScheduleWeekGrid
            events={events}
            timeSlots={new Array(6).fill("")}
            weekDays={weekDays}
            gutterLabel=""
            compact
            selectedId={selectedEvent?.id ?? null}
            onSelect={openEvent}
          />
        </div>
      );
  }

  return (
    <>
      {grid}
      <ScheduleEventDetailDialog
        event={selectedEvent}
        open={detailOpen}
        onOpenChange={setDetailOpen}
      />
    </>
  );
}

function EmptyMilestones() {
  return (
    <p className="text-sm text-muted-foreground">
      No booking milestones in this window.
    </p>
  );
}

export function KaiScheduleSnapshot({
  spec,
}: {
  spec: KaiScheduleSnapshotSpec;
}) {
  const title =
    spec.title?.trim() ||
    (spec.mode === "booking_milestones" ? "Booking schedule" : "Sailings");

  return (
    <div className="glass-panel my-2 flex min-w-0 flex-col gap-3 overflow-hidden rounded-xl p-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="min-w-0 flex-1 text-base font-semibold text-foreground">
          {title}
        </p>
        {spec.context.carrier && (
          <Badge variant="info" className="shrink-0 px-2 py-0.5 text-[10px]">
            {spec.context.carrier}
          </Badge>
        )}
      </div>

      {spec.mode === "booking_milestones" ? (
        <MilestoneGrid spec={spec} />
      ) : (
        <SailingList
          schedules={spec.schedules}
          showMatch={spec.mode === "booking_matched"}
        />
      )}

      {spec.footnotes && spec.footnotes.length > 0 && (
        <ul className="space-y-1 text-xs text-muted-foreground">
          {spec.footnotes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      )}

      <div className="flex justify-end border-t border-border/50 pt-2">
        <Link
          to={spec.open_in_schedule_url}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
        >
          Open in Schedule
          <ArrowUpRight className="size-3.5" />
        </Link>
      </div>
    </div>
  );
}
